import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { useAppDispatch, useAppSelector } from "@src/store/hooks";
import { selectSelectedInventoryItems } from "@src/store/pricing/queryEventInventoryAllSlice";
import { pricingActions } from "@src/store";
import { Button, ButtonVariant } from "../button/Button";
import { Switch } from "../switch/Switch";
import { clx } from "../utils/stringUtils";
import EventContextHeader from "@src/components/eventContextHeader/EventContextHeader";
import BulkEditSectionRowItem from "./BulkEditSectionRowItem";
import styles from "./BulkEditSectionRow.module.scss";

type TBulkEditSectionRow = {
	handleClose?: () => void;
};

const BulkEditSectionRow = ({ handleClose }: TBulkEditSectionRow) => {
	const dispatch = useAppDispatch();

	const items = useAppSelector(selectSelectedInventoryItems);
	const submitting = useAppSelector((state) => state.pricing.updateListingPositionBulk.loading);

	const [removedIds, setRemovedIds] = useState<Array<number>>([]);
	const workingItems = useMemo(() => items.filter((x) => !removedIds.includes(x.Listing_ID)), [items, removedIds]);

	const [editSection, setEditSection] = useState(false);
	const [sectionValue, setSectionValue] = useState("");
	const [editRow, setEditRow] = useState(false);
	const [rowValue, setRowValue] = useState("");

	const initialLocksRef = useRef<{ section: boolean; row: boolean; seat: boolean } | null>(null);
	if (initialLocksRef.current === null && items.length > 0) {
		initialLocksRef.current = {
			section: items.every((x) => !!x.Section_Locked),
			row: items.every((x) => !!x.Row_Locked),
			seat: items.every((x) => !!x.Seat_Locked),
		};
	}
	const initialLocks = initialLocksRef.current ?? { section: false, row: false, seat: false };

	const [lockSection, setLockSection] = useState(initialLocks.section);
	const [lockRow, setLockRow] = useState(initialLocks.row);
	const [lockSeats, setLockSeats] = useState(initialLocks.seat);

	useEffect(() => {
		if (!editSection) setSectionValue("");
	}, [editSection]);

	useEffect(() => {
		if (!editRow) setRowValue("");
	}, [editRow]);

	const handleRemoveItem = (listingId: number) => {
		setRemovedIds((prev) => (prev.includes(listingId) ? prev : [...prev, listingId]));
	};

	const sectionPopulated = editSection && sectionValue.trim().length > 0;
	const rowPopulated = editRow && rowValue.trim().length > 0;
	const locksChanged =
		lockSection !== initialLocks.section || lockRow !== initialLocks.row || lockSeats !== initialLocks.seat;

	const canSave = workingItems.length > 0 && (sectionPopulated || rowPopulated || locksChanged) && !submitting;

	const handleSubmit = async () => {
		if (!canSave) return;
		try {
			await dispatch(
				pricingActions.updateListingPositionBulk({
					ListingIds: workingItems.map((x) => x.Listing_ID),
					NewSection: editSection ? sectionValue.trim() : undefined,
					NewRow: editRow ? rowValue.trim() : undefined,
					SectionLocked: lockSection,
					RowLocked: lockRow,
					SeatLocked: lockSeats,
				}),
			).unwrap();
			toast.success("Listings updated.");
			handleClose?.();
		} catch {
			toast.error("Failed to update listings.");
		}
	};

	const targetSection = editSection ? sectionValue.trim() : null;
	const targetRow = editRow ? rowValue.trim() : null;

	const mappedItems = workingItems.map((x) => (
		<BulkEditSectionRowItem
			key={x.Item_ID}
			data={x}
			targetSection={targetSection}
			targetRow={targetRow}
			onRemove={handleRemoveItem}
		/>
	));

	return (
		<div className={styles.root}>
			<EventContextHeader
				padding
				listings={workingItems.map((x) => ({ section: x.Section, row: x.Row, seats: x.Seats }))}
			/>
			<div className={styles.top}>
				<div className={styles.left}>
					<div className={clx(styles.subHeader, styles.mb)}>Location Updates</div>

					<div className={styles.toggleRow}>
						<div className={styles.toggleButton}>
							<Switch checked={editSection} onChange={() => setEditSection((v) => !v)} label="Edit section" />
						</div>
						<input
							className={styles.input}
							type="text"
							placeholder="New section"
							value={sectionValue}
							disabled={!editSection}
							onChange={(e) => setSectionValue(e.target.value)}
						/>
					</div>

					<div className={styles.toggleRow}>
						<div className={styles.toggleButton}>
							<Switch checked={editRow} onChange={() => setEditRow((v) => !v)} label="Edit row" />
						</div>
						<input
							className={styles.input}
							type="text"
							placeholder="New row"
							value={rowValue}
							disabled={!editRow}
							onChange={(e) => setRowValue(e.target.value)}
						/>
					</div>

					<div className={clx(styles.subHeader, styles.locksHeader)}>Location Locks</div>
					<div className={styles.helperText}>
						Locking these attributes prevents external POS systems from dynamically overwriting your local manual
						changes.
					</div>
					<div className={styles.locksRow}>
						<div className={styles.toggleButton}>
							<Switch checked={lockSection} onChange={() => setLockSection((v) => !v)} label="Lock section" />
						</div>
						<div className={styles.toggleButton}>
							<Switch checked={lockRow} onChange={() => setLockRow((v) => !v)} label="Lock row" />
						</div>
						<div className={styles.toggleButton}>
							<Switch checked={lockSeats} onChange={() => setLockSeats((v) => !v)} label="Lock seats" />
						</div>
					</div>
				</div>

				<div className={styles.right}>
					<div className={styles.rightTopHeaders}>
						<div className={styles.subHeader}>Changes Summary</div>
						<div className={styles.subHeaderSecondary}>Applying to {workingItems.length} items</div>
					</div>
					<div data-no-border="true" className={styles.singleListing}>
						<div className={clx(styles.eventCol, styles.headerCol)}>Event Name</div>
						<div className={styles.valueCol}>Current</div>
						<div className={styles.arrowCol}></div>
						<div className={styles.valueCol}>Target</div>
						<div className={styles.xContainer}></div>
					</div>
					<div className={styles.listings}>{mappedItems}</div>
				</div>
			</div>
			<div className={styles.buttons}>
				<Button onClick={handleClose} variant={ButtonVariant.Secondary} disabled={submitting}>
					Cancel
				</Button>
				<Button onClick={handleSubmit} isLoading={submitting} disabled={!canSave}>
					Save changes
				</Button>
			</div>
		</div>
	);
};

export default BulkEditSectionRow;
